/**
 * features/platform/branding — utilitários de aplicação de branding white-label.
 *
 * Converte as cores do tenant (PortalBranding) em CSS custom properties consumidas
 * pelos design tokens do portal (`--bg-accent`, `--bg-accent-hover`, `--fg-accent`,
 * `--fg-on-accent` e o token de marca secundária `--brand-secondary`).
 *
 * As cores primária/secundária vêm do backend tenant-administration
 * (GET /v1/tenant/branding → colors.primary / colors.secondary). Quando ausentes,
 * o branding default do design system permanece (não sobrescreve).
 */

import type { PortalBranding } from "../../data/repos/platformRepo.js";
import { contrastRatio, WCAG_AA_NORMAL } from "../../adapters/branding/contrastSpecification.js";

/** Mapa de CSS custom properties derivadas do branding. */
export type BrandingCssVars = Readonly<Record<string, string>>;

/**
 * Subconjunto de campos que estas funções realmente leem — `primaryColor`/`secondaryColor`.
 * `PortalBranding` (autenticado, RequiresTenantAdmin) e `PortalPublicBranding` (anônimo,
 * brand.json — ver `usePublicBranding()`) satisfazem este tipo igualmente; aceitar o subconjunto
 * em vez de `PortalBranding` inteiro é o que permite `BrandingApplier` aplicar as cores a partir
 * do endpoint público sem depender de campos (`wcagContrastOk`, `updatedAt`, …) que só existem
 * na tela de edição autenticada.
 */
export type BrandingColorSource = Pick<PortalBranding, "primaryColor" | "secondaryColor">;

const HEX_RE = /^#([0-9a-f]{6})$/i;

/** Normaliza e valida uma cor hex (#RRGGBB). Retorna null se inválida. */
function normalizeHex(color: string | null | undefined): string | null {
  if (!color) return null;
  const trimmed = color.trim();
  return HEX_RE.test(trimmed) ? trimmed.toLowerCase() : null;
}

/** Ajusta o brilho de uma cor hex por um fator (-1..1). Negativo escurece. */
export function shadeHex(hex: string, factor: number): string {
  const m = HEX_RE.exec(hex);
  if (!m) return hex;
  const value = m[1] ?? "";
  const channels = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
  const adjusted = channels.map((c) => {
    const target = factor < 0 ? 0 : 255;
    const next = Math.round(c + (target - c) * Math.abs(factor));
    return Math.max(0, Math.min(255, next));
  });
  return "#" + adjusted.map((c) => c.toString(16).padStart(2, "0")).join("");
}

/** Luminância relativa (WCAG) para decidir cor de texto sobre a cor de marca. */
function relativeLuminance(hex: string): number {
  const m = HEX_RE.exec(hex);
  if (!m) return 0;
  const value = m[1] ?? "";
  const srgb = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16) / 255);
  const linear = srgb.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * (linear[0] ?? 0) + 0.7152 * (linear[1] ?? 0) + 0.0722 * (linear[2] ?? 0);
}

/** Distância euclidiana entre duas cores hex, no espaço RGB (0-255 por canal). */
function rgbDistance(hexA: string, hexB: string): number {
  const a = HEX_RE.exec(hexA);
  const b = HEX_RE.exec(hexB);
  if (!a || !b) return Infinity;
  const va = a[1] ?? "";
  const vb = b[1] ?? "";
  let sumOfSquares = 0;
  for (const i of [0, 2, 4]) {
    const ca = parseInt(va.slice(i, i + 2), 16);
    const cb = parseInt(vb.slice(i, i + 2), 16);
    sumOfSquares += (ca - cb) ** 2;
  }
  return Math.sqrt(sumOfSquares);
}

/** Um candidato por nível de canal (8 bits) em cada direção — a resolução máxima que
 * `shadeHex` produz, já que ele arredonda cada canal para inteiro 0-255. */
const TONE_SEARCH_STEPS = 255;

/**
 * Sugere o tom mais próximo de `hex` — clareando ou escurecendo pelo mesmo caminho de
 * `shadeHex` — que atinge o limiar de contraste `threshold` contra `foregroundHex` (REQ-13).
 *
 * Não usa lista fixa de cores: varre exaustivamente os tons alcançáveis por `shadeHex` nas
 * duas direções (um candidato por nível de canal) e devolve o de MENOR distância euclidiana
 * (RGB) até a cor original, entre os que passam no limiar. `null` só quando NENHUM tom ao
 * longo do eixo de brilho passa — não deveria acontecer para os textos de botão deste design
 * system (bem escuros: o extremo branco quase sempre aprova), mas o chamador não deve supor
 * que a sugestão sempre existe.
 *
 * Quando `hex` JÁ passa no limiar, o próprio `hex` é o tom de menor distância possível
 * (distância zero) — devolvido sem busca.
 */
export function suggestAccessibleTone(
  hex: string,
  foregroundHex: string,
  threshold: number = WCAG_AA_NORMAL,
): string | null {
  const base = normalizeHex(hex);
  if (!base) return null;

  let baseRatio: number;
  try {
    baseRatio = contrastRatio(foregroundHex, base);
  } catch {
    return null;
  }
  if (baseRatio >= threshold) return base;

  let best: { hex: string; distance: number } | null = null;
  for (let i = 1; i <= TONE_SEARCH_STEPS; i += 1) {
    const factor = i / TONE_SEARCH_STEPS;
    for (const sign of [1, -1] as const) {
      const candidate = shadeHex(base, sign * factor);
      let ratio: number;
      try {
        ratio = contrastRatio(foregroundHex, candidate);
      } catch {
        continue;
      }
      if (ratio < threshold) continue;
      const distance = rgbDistance(base, candidate);
      if (!best || distance < best.distance) {
        best = { hex: candidate, distance };
      }
    }
  }
  return best?.hex ?? null;
}

/**
 * Deriva as CSS custom properties a partir do branding do tenant.
 * Retorna um objeto vazio quando não há cor primária válida (mantém o default).
 */
export function deriveBrandingCssVars(branding: BrandingColorSource | null | undefined): BrandingCssVars {
  if (!branding) return {};

  const primary = normalizeHex(branding.primaryColor);
  const secondary = normalizeHex(branding.secondaryColor);

  const vars: Record<string, string> = {};

  if (primary) {
    vars["--bg-accent"] = primary;
    vars["--bg-accent-hover"] = shadeHex(primary, -0.18);
    vars["--bg-accent-soft"] = shadeHex(primary, 0.85);
    vars["--bg-accent-soft-2"] = shadeHex(primary, 0.78);
    vars["--fg-accent"] = shadeHex(primary, -0.1);
    vars["--fg-on-accent"] = relativeLuminance(primary) > 0.5 ? "#0b0c18" : "#ffffff";
    vars["--brand-primary"] = primary;
  }

  if (secondary) {
    vars["--brand-secondary"] = secondary;
  }

  return vars;
}

/**
 * Aplica as CSS custom properties derivadas do branding no elemento raiz (<html>).
 * Idempotente: chamar novamente atualiza os valores. Não remove o default quando
 * não há cores (apenas não escreve nada).
 */
export function applyBrandingCssVars(
  branding: BrandingColorSource | null | undefined,
  root: HTMLElement = document.documentElement,
): BrandingCssVars {
  const vars = deriveBrandingCssVars(branding);
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  return vars;
}
